import { shuffleArray, getRandomInt, arraysAreEqual } from 'root/lib/utilityFunctions';

import { GAME_ID, REDUX_ACTIONS } from '../../data/constants';
import { WordGrammarPositions } from '../../data/enums';
import {
  isValidPrimarySentence,
  isValidSecondarySentence,
  updateGrammarState,
  getUserResponse,
  calculateOrder,
} from '../../lib/utilityFunctions';

const newPositionTracker = {
  // a value that indicates where along the linear progression of words this tracker lives.
  // Parallels the "order" property of a flexbox item.
  order: Number.MAX_SAFE_INTEGER,
  // the actual physical position that this positionTracker lives at
  destX: 0,
  destY: 0,

  startX: 0,
  startY: 0,
  // the width of the draggable word that this object is tracking
  width: 0,

  // values that denote the order of the tracked draggables immediate neighbors.
  // if the word exits this range while being dragged, it will trigger a sorting of the orders array.
  leftNeighborOrder: Number.MAX_SAFE_INTEGER,
  rightNeighborOrder: Number.MAX_SAFE_INTEGER,

  // whether or not the tracked draggable is actually being dragged or not.
  beingDragged: false,
  isDraggable: false,
  rotationOffset: 0,
  dragMoveX: 0,
  dragMoveY: 0,
  snappedIntoBox: false,

  // the index of this object inside of the trackerProps
  myIndex: undefined, // should be set when a new copy of this object is built.
  // the indices of all trackers which are glued to this one via green circles.
  connectedIndices: [],
  hasLeftLink: false,
  hasRightLink: false,

  grammarState: WordGrammarPositions.MiddleWord,
  punctuation: null,
};

const initialState = {
  trackerProps: [], // one object from this array will drive the props of each draggable word
  orders: [], // this array will not drive anybody's props. It will be sorted when appropriate and used to drive the values of the objects in trackerProps.
  allWidthsSet: false,
  allWordsInBox: false,
  positionOfSentenceZone: { x: 0, y: 0 },

  correctOrder: false,
  checkedForCorrectOrder: false,
  isSecondarySentenceCorrect: false, // this will only be checked on the first attempt
};

const positionsReducer = (state = initialState, action) => {
  if (action.gameId !== GAME_ID) {
    return state;
  }

  switch (action.type) {
    case REDUX_ACTIONS.SET_SENTENCE_ZONE_POSITION:
      return {
        ...state,
        positionOfSentenceZone: { ...action.position },
      };

    case REDUX_ACTIONS.BEGIN_ROUND:
      return {
        ...state,
        allWordsInBox: false,
      };

    case REDUX_ACTIONS.NEW_ITEM: {
      const trackerProps = [];
      const orders = [];

      const [primarySentence] = action.targetSentences || [];
      const wordList = primarySentence?.sentence?.split(' ') || [];

      for (let i = 0; i < action.count; i++) {
        trackerProps.push({
          ...newPositionTracker,
          myIndex: i,
          connectedIndices: [i],
        });
        orders.push({
          order: Number.MAX_SAFE_INTEGER,
          indexInTrackerProps: i,
          text: wordList[i],
        });
      }

      return {
        ...state,
        trackerProps,
        orders,
        allWidthsSet: false,
        allWordsInBox: false,
        checkedForCorrectOrder: false,
        correctOrder: false,
        isSecondarySentenceCorrect: false,
        targetSentences: action.targetSentences,
      };
    }

    case REDUX_ACTIONS.CHECK_FOR_CORRECT: {
      const { orders, targetSentences } = state;
      const trackerData = [...state.trackerProps];
      const isFirstAttempt = !state.checkedForCorrectOrder;
      const userResponse = getUserResponse(orders);

      const isPrimarySentenceCorrect = isValidPrimarySentence(orders);
      const isSecondarySentenceCorrect = isFirstAttempt
        ? isValidSecondarySentence(userResponse, targetSentences)
        : false;
      const correctOrder = isPrimarySentenceCorrect || isSecondarySentenceCorrect;

      const trackerProps = correctOrder
        ? updateGrammarState(trackerData, userResponse, orders, targetSentences)
        : trackerData;

      return {
        ...state,
        correctOrder,
        checkedForCorrectOrder: true,
        trackerProps,
        isSecondarySentenceCorrect,
      };
    }

    case REDUX_ACTIONS.ATTACH_ADJACENT_WORDS: {
      const trackerProps = [...state.trackerProps];

      if (state.isSecondarySentenceCorrect) {
        let runningWidth = 0;
        state.orders.forEach(({ indexInTrackerProps }, index) => {
          const currentTracker = trackerProps[indexInTrackerProps];
          trackerProps[indexInTrackerProps] = {
            ...currentTracker,
            hasLeftLink: index !== 0,
            hasRightLink: index !== state.orders.length - 1,
            rotationOffset: runningWidth,
          };
          runningWidth += currentTracker.width;
        });

        return {
          ...state,
          trackerProps,
        };
      }

      const startEndConnectionPairs = [];
      let prevTrackerIndex = null;
      let startPoint = null;
      for (let i = 0; i < state.orders.length; i++) {
        const currentTrackerIndex = state.orders[i].indexInTrackerProps;
        if (prevTrackerIndex !== null) {
          if (currentTrackerIndex === prevTrackerIndex + 1) {
            if (startPoint === null) {
              startPoint = prevTrackerIndex;
            }
          } else if (startPoint != null) {
            startEndConnectionPairs.push([startPoint, prevTrackerIndex]);
            startPoint = null;
          }
        }
        prevTrackerIndex = currentTrackerIndex;
      }
      if (startPoint != null) {
        startEndConnectionPairs.push([startPoint, prevTrackerIndex]);
      }

      for (let i = 0; i < startEndConnectionPairs.length; i++) {
        const [start, end] = startEndConnectionPairs[i];
        let runningWidth = 0;
        const connections = new Array(end - start + 1)
          .fill(0)
          .map((_, c) => c + start);
        for (let j = start; j <= end; j++) {
          const o = { ...trackerProps[j] };
          o.hasLeftLink = j !== start;
          o.hasRightLink = j !== end;
          o.connectedIndices = [...connections];
          o.rotationOffset = runningWidth;
          runningWidth += o.width;
          trackerProps[j] = o;
        }
      }

      return {
        ...state,
        trackerProps,
      };
    }

    case REDUX_ACTIONS.SET_TRACKER_WIDTH: {
      const trackerProps = [...state.trackerProps];
      let obj = trackerProps[action.index];
      const dx = action.width - obj.width;
      // set the width of the new tracker
      trackerProps[action.index] = { ...obj, width: action.width };
      // push everybody to the right of the new tracker over to the side by an appropriate amount.
      for (let i = action.index + 1; i < trackerProps.length; i++) {
        obj = trackerProps[i];
        trackerProps[i] = { ...obj, destX: obj.destX + dx };
      }

      let allWidthsSet = true;
      for (let i = 0; i < trackerProps.length; i++) {
        if (trackerProps[i].width === 0) {
          allWidthsSet = false;
          break;
        }
      }

      if (allWidthsSet) {
        const yStart = 340;
        const xStart = 60;
        const xMax = 860;
        let destX = xStart;
        let destY = yStart;

        const inOrder = new Array(trackerProps.length).fill(0).map((_, i) => i);
        const randomOrderOfIndices = shuffleArray(inOrder);
        for (let r = 0; r < randomOrderOfIndices.length; r++) {
          const i = randomOrderOfIndices[r];
          let o = trackerProps[i];
          const randomY = destY + getRandomInt(-5, 5);
          o = {
            ...o,
            destX,
            startX: destX,
            destY: randomY,
            startY: randomY,
          };

          trackerProps[i] = o;

          destX += o.width + 25;
          if (destX > xMax) {
            destX = xStart;
            destY += 80;
          }
        }
      }

      return {
        ...state,
        trackerProps,
        allWidthsSet,
      };
    }

    case REDUX_ACTIONS.SET_TRACKER_ORDER: {
      let orders = [...state.orders];
      let trackerProps = [...state.trackerProps];

      const indicesToUpdate = [...trackerProps[action.index].connectedIndices];

      for (let i = 0; i < orders.length; i++) {
        if (indicesToUpdate.includes(orders[i].indexInTrackerProps)) {
          // if this tracker is connected to the draggable that wants to update...
          orders[i] = { ...orders[i], order: action.order };
          let trackerInstance = trackerProps[orders[i].indexInTrackerProps];
          trackerInstance = { ...trackerInstance, order: action.order };
          trackerProps[orders[i].indexInTrackerProps] = trackerInstance;
        } else if (
          trackerProps[orders[i].indexInTrackerProps].connectedIndices.length >
          1
        ) {
          // "collapse" groups of connected trackers into tight spaces.
          // they will "expand" again shortly in setDestinations and ensure that everybody is positioned correctly w/o breaking links
          let trackerInstance = trackerProps[orders[i].indexInTrackerProps];
          const [collapsedOrderIndex] = trackerInstance.connectedIndices;
          const collapsedOrder = trackerProps[collapsedOrderIndex].order;
          orders[i] = { ...orders[i], order: collapsedOrder };
          trackerInstance = { ...trackerInstance, order: collapsedOrder };
          trackerProps[orders[i].indexInTrackerProps] = trackerInstance;
        }
      }

      orders.sort((a, b) => {
        if (a.order < b.order) {
          return -1;
        }
        if (a.order > b.order) {
          return 1;
        }
        if (a.order === b.order) {
          if (a.indexInTrackerProps < b.indexInTrackerProps) {
            return -1;
          }
          if (a.indexInTrackerProps > b.indexInTrackerProps) {
            return 1;
          }
        }
        return 0;
      });

      const arrays = setDestinations(
        orders,
        trackerProps,
        state.positionOfSentenceZone,
      );
      trackerProps = arrays.trackerProps;
      orders = arrays.orders;

      return {
        ...state,
        trackerProps,
        orders,
      };
    }

    case REDUX_ACTIONS.SET_BEING_DRAGGED: {
      const trackerProps = [...state.trackerProps];
      trackerProps[action.index] = {
        ...trackerProps[action.index],
        beingDragged: action.beingDragged,
      };

      let allWordsInBox = true;
      for (let i = 0; i < trackerProps.length; i++) {
        if (!trackerProps[i].snappedIntoBox) {
          allWordsInBox = false;
          break;
        }
      }

      return {
        ...state,
        trackerProps,
        allWordsInBox,
      };
    }

    case REDUX_ACTIONS.SET_IS_DRAGGABLE: {
      const trackerProps = [...state.trackerProps];
      trackerProps[action.index] = {
        ...trackerProps[action.index],
        isDraggable: action.isDraggable,
      };

      return {
        ...state,
        trackerProps,
      };
    }

    case REDUX_ACTIONS.DRAG_MOVE: {
      const trackerProps = [...state.trackerProps];
      const o = { ...trackerProps[action.index] };
      o.dragMoveX = action.dragMoveX;
      o.dragMoveY = action.dragMoveY;
      trackerProps[action.index] = o;
      return {
        ...state,
        trackerProps,
      };
    }

    default:
      return state;
  }
};

function setDestinations(_orders, _trackerProps, positionOfSentenceZone) {
  const orders = [..._orders];
  const trackerProps = [..._trackerProps];
  let runningX = positionOfSentenceZone.x + 25;
  let runningY = positionOfSentenceZone.y + 20;

  // first we need to find everybody's new positions and their new orders to match those positions
  for (let i = 0; i < orders.length; i++) {
    const currentIndex = orders[i].indexInTrackerProps;
    const o = { ...trackerProps[currentIndex] };

    if (
      runningX + o.width >
      positionOfSentenceZone.x + positionOfSentenceZone.width - 20
    ) {
      runningX = positionOfSentenceZone.x + 25;
      runningY += positionOfSentenceZone.height / 2 - 10;
    }

    if (orders[i].order < Number.MAX_SAFE_INTEGER) {
      o.destX = runningX;
      o.destY = runningY;
      const newOrder = calculateOrder(
        runningX,
        runningY,
        positionOfSentenceZone,
      );
      o.order = newOrder;
      o.snappedIntoBox = true;
      orders[i] = { ...orders[i], order: newOrder };
    } else {
      o.destX = o.startX;
      o.destY = o.startY;
      o.snappedIntoBox = false;
      o.order = Number.MAX_SAFE_INTEGER;
    }

    runningX += o.width + 10;
    trackerProps[currentIndex] = o;
  }

  // once we've calculated everybody's order, we can update people's neighbor orders
  for (let i = 0; i < orders.length; i++) {
    const currentIndex = orders[i].indexInTrackerProps;

    const o = { ...trackerProps[currentIndex] };
    const arr1 = o.connectedIndices;
    // figure out the group to the left and the right of the current group.
    let leftIndex = null;
    for (let attempt = i; attempt >= 0; attempt--) {
      const orderAttempt = orders[attempt];
      const arr2 =
        trackerProps[orderAttempt.indexInTrackerProps].connectedIndices;
      if (!arraysAreEqual(arr1, arr2)) {
        leftIndex = orders[attempt].indexInTrackerProps;
        break;
      }
    }
    let rightIndex = null;
    for (let attempt = i; attempt < orders.length; attempt++) {
      const orderAttempt = orders[attempt];
      const arr2 =
        trackerProps[orderAttempt.indexInTrackerProps].connectedIndices;
      if (!arraysAreEqual(arr1, arr2)) {
        rightIndex = orders[attempt].indexInTrackerProps;
        break;
      }
    }

    if (leftIndex !== null) {
      const leftGroup = trackerProps[leftIndex].connectedIndices;
      const [leftMostTrackerInGroup] = leftGroup;
      const leftMostOrderInGroup = orders.find(element => element.indexInTrackerProps === leftMostTrackerInGroup);
      const neighborOrder = leftMostOrderInGroup.order;
      o.leftNeighborOrder = neighborOrder;
    } else {
      // the leftmost pos. tracker has no left neighbor, so we'll just pretend one lives way off in the abyss to the left.
      o.leftNeighborOrder = Number.MIN_SAFE_INTEGER;
    }

    if (rightIndex !== null) {
      const rightGroup = trackerProps[rightIndex].connectedIndices;
      const rightMostTrackerInGroup = rightGroup[rightGroup.length - 1];
      const rightMostOrderInGroup = orders.find(element => element.indexInTrackerProps === rightMostTrackerInGroup);
      const neighborOrder = rightMostOrderInGroup.order;
      o.rightNeighborOrder = neighborOrder;
    } else {
      // the rightmost group of trackers will have a pretend neighbor to the abyss on its right.
      o.rightNeighborOrder = Number.MAX_SAFE_INTEGER;
    }

    trackerProps[currentIndex] = o;
  }

  return {
    trackerProps,
    orders,
  };
}

export default positionsReducer;
